// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file OverflowList.tsx
 * @input Uses React, StyleX, useOverflow hook
 * @output Exports OverflowList component and OverflowListProps type
 * @position Core implementation; consumed by index.ts
 *
 * Renders a horizontal list of items, hiding those that don't fit in the
 * available width and optionally showing an overflow indicator.
 * Uses a hidden measurement container to avoid flickering.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/OverflowList/index.ts (exports if types change)
 * - /packages/cli/templates/blocks/components/OverflowList/ (showcase blocks)
 */
import { Children } from 'react';
import * as stylex from '@stylexjs/stylex';
import { mergeProps, mergeRefs } from "../utils/index.js";
import { useOverflow } from "../hooks/useOverflow.js";
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  container: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kVQacm: "xb3r6kr",
    khDVqt: "xuxw1ft",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  fillParent: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  measureContainer: {
    kVAEAm: "x10l6tqk",
    k33iCy: "xlshs6z",
    kZKoxP: "xqtp20y",
    kVQacm: "xb3r6kr",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    khDVqt: "xuxw1ft",
    kfzvcC: "x47corl",
    $$css: true
  }
};
const gapStyles = {
  "0": {
    kOIVth: "xsn7fz1",
    $$css: true
  },
  "1": {
    kOIVth: "xzye2dw",
    $$css: true
  },
  "2": {
    kOIVth: "x1txdalj",
    $$css: true
  },
  "3": {
    kOIVth: "xjcht0a",
    $$css: true
  },
  "4": {
    kOIVth: "x18g69wz",
    $$css: true
  },
  "5": {
    kOIVth: "x9mgr7n",
    $$css: true
  },
  "6": {
    kOIVth: "x1qh66ti",
    $$css: true
  },
  "8": {
    kOIVth: "x4t41sb",
    $$css: true
  },
  "10": {
    kOIVth: "x3hoi3v",
    $$css: true
  },
  "0.5": {
    kOIVth: "x1lsbc85",
    $$css: true
  },
  "1.5": {
    kOIVth: "x1s4dlld",
    $$css: true
  }
};

/**
 * Maps spacing token steps to pixel values for overflow calculations.
 */
const spacingToPx = {
  0: 0,
  0.5: 2,
  1: 4,
  1.5: 6,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40
};
/**
 * A horizontal list that hides items that don't fit and shows an overflow indicator.
 *
 * Uses a hidden measurement container to determine which items fit without
 * causing visual flickering. The overflow indicator is also measured
 * automatically so no manual width value is needed.
 *
 * @example
 * ```
 * <OverflowList
 *   gap={2}
 *   overflowRenderer={(items) => (
 *     <Button label={`+${items.length} more`} variant="ghost" />
 *   )}>
 *   <Button label="Action 1" />
 *   <Button label="Action 2" />
 *   <Button label="Action 3" />
 *   <Button label="Action 4" />
 * </OverflowList>
 * ```
 */
export function OverflowList({
  children,
  gap = 2,
  minVisibleItems = 0,
  collapseFrom = 'end',
  behavior = 'observeSelf',
  overflowRenderer,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const childArray = Children.toArray(children);
  const itemCount = childArray.length;
  const gapPx = spacingToPx[gap];
  const observeParent = behavior === 'observeParent';
  const {
    containerRef,
    measureRef,
    visibleCount,
    hasOverflow
  } = useOverflow(itemCount, {
    gap: gapPx,
    minVisibleItems,
    collapseFrom,
    behavior
  });
  const allItems = childArray.map((child, index) => ({
    child,
    index
  }));
  let visibleItems;
  let overflowItems;
  if (collapseFrom === 'end') {
    visibleItems = allItems.slice(0, visibleCount);
    overflowItems = allItems.slice(visibleCount);
  } else {
    visibleItems = allItems.slice(itemCount - visibleCount);
    overflowItems = allItems.slice(0, itemCount - visibleCount);
  }

  // Render a placeholder for measurement — uses all items as overflow
  // so the indicator renders at its maximum possible width.
  const measureIndicator = overflowRenderer?.(allItems);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: measureRef,
      "aria-hidden": "true",
      inert: true,
      ...stylex.props(styles.measureContainer, gapStyles[gap]),
      children: [childArray, measureIndicator != null && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x3nfvp2"
        },
        children: measureIndicator
      })]
    }), /*#__PURE__*/_jsxs("div", {
      ref: mergeRefs(ref, containerRef),
      ...mergeProps(themeProps('overflow-list'), stylex.props(styles.container, gapStyles[gap], observeParent && hasOverflow && styles.fillParent, xstyle), className, style),
      ...props,
      children: [collapseFrom === 'start' && hasOverflow && overflowRenderer?.(overflowItems), visibleItems.map(({
        child
      }) => child), collapseFrom === 'end' && hasOverflow && overflowRenderer?.(overflowItems)]
    })]
  });
}
OverflowList.displayName = 'OverflowList';